"use client";

import React, { useEffect, useRef, useCallback } from "react";
import type { INewsItem } from "../../../interfaces/News.interface";
import type { NewsAEMOtherNews } from "../../../interfaces/NewsAEM.interface";
import CompactNewsCard from "../components/CompactNewsCard";
import NewsEmptyState from "../components/NewsEmptyState";
import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";
import type { INewsFilters } from "../../../interfaces/NewsFilter.interface";
import ListWithShowMore from "../components/List";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { useOtherNews } from "../hooks/useOtherNews";
import { newsStore } from "../store/newsStore";
import { trackComponent } from "@repo/analytics";
import {
  componentNameConstants,
  dlCtaTextConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import {
  campaignName,
  journeyTag,
  otherNewsEntryPoint,
  productCategory,
  propertyType,
  referenceId,
  sprint,
  subSection,
} from "../../../pages/News/newsTrackingConstants";
import { trackFailureScenarioRetryClick } from "../components/trackFailureScenarioRetry";

interface OtherNewsProps {
  initialNews?: INewsItem[] | null;
  aemContent?: NewsAEMOtherNews | null;
  filters?: string[];
  limit?: number;
  componentPosition?: string;
  newsFilters?: INewsFilters;
}

const OtherNews: React.FC<OtherNewsProps> = ({
  initialNews,
  aemContent,
  filters = [],
  limit = 5,
  componentPosition,
  newsFilters = { events: [], index: [], instrument: [], marketCap: [], sector: [] },
}) => {
  const {
    news,
    isLoading,
    hasError,
    page,
    showMore,
    showLess,
    handleShowMore,
    handleShowLess,
    refetch,
  } = useOtherNews({
    initialNews: initialNews ?? [],
    filters,
    limit,
  });

  const scrollRestoredRef = useRef(false);
  const newsListRef = useRef<HTMLDivElement>(null);
  const filtersKeyRef = useRef<string>("");

  // Memoize filters key to avoid complex expression in dependency array
  // Create a sorted copy using localeCompare for reliable alphabetical sorting
  const sortedFilters = [...filters].sort((a: string, b: string) => a.localeCompare(b));
  const filtersKey = sortedFilters.join(",");

  // Reset scroll restoration flag when filters change
  useEffect(() => {
    if (filtersKeyRef.current !== filtersKey) {
      scrollRestoredRef.current = false;
      filtersKeyRef.current = filtersKey;
    }
  }, [filtersKey]);

  // Helper function to perform the actual scroll restoration
  const performScrollRestoration = useCallback((scrollY: number) => {
    // Verify the page height is sufficient for the scroll position
    const pageHeight = document.documentElement.scrollHeight;
    const targetScroll = Math.min(scrollY, pageHeight - window.innerHeight);
    
    if (targetScroll > 0) {
      window.scrollTo({
        top: targetScroll,
        behavior: "instant" as ScrollBehavior,
      });
    }
    
    // Clear scroll position after restoration
    newsStore.clearScrollPosition();
    scrollRestoredRef.current = true;
  }, []);

  // Helper function to schedule scroll restoration with double requestAnimationFrame
  const scheduleScrollRestoration = useCallback((scrollY: number) => {
    // Use multiple requestAnimationFrame calls to ensure layout is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        performScrollRestoration(scrollY);
      });
    });
  }, [performScrollRestoration]);

  // Restore scroll position after news list is rendered
  useEffect(() => {
    // Only restore once per filter set, and only if we have news items and not loading
    if (scrollRestoredRef.current || news.length === 0 || isLoading) {
      return;
    }

    // Wait for DOM to be fully rendered before restoring scroll
    const restoreScroll = () => {
      const scrollY = newsStore.getScrollPosition();
      if (scrollY !== null && scrollY > 0) {
        scheduleScrollRestoration(scrollY);
      } else {
        // No scroll position to restore, mark as done
        scrollRestoredRef.current = true;
      }
    };

    // Small delay to ensure all content is rendered and layout is stable
    const timeoutId = setTimeout(restoreScroll, 150);
    return () => clearTimeout(timeoutId);
  }, [news.length, isLoading, filtersKey, scheduleScrollRestoration]);

  // Render section header component
  const renderHeader = () => (
    <>
      <h2 className="font-rubik mb-[4px] text-[18px] font-medium leading-[100%] tracking-[0px] text-[#1A1A1A] md:mb-[8px] md:text-[20px]">
        {aemContent?.componentTitle ?? "Other news"}
      </h2>
      <p className="mb-[12px] text-[12px] leading-none text-gray-600 md:mb-[20px] md:text-[14px] md:leading-[20px]">
        {aemContent?.subTitle ?? "Get updates about stocks"}
      </p>
    </>
  );

  const handleShowMoreWithCt = () => {
    (async () => {
      const customerId = await getCustomerIdForAnalytics();

      const trackPayload = {
        componentName: componentNameConstants.NEWS_CATEGORY_LISTING,
        sectionTitle: aemContent?.componentTitle ?? "Other news",
        eventType: dlEventConstants.NEWS_CATEGORY_LISTING_BUTTON_CLICK,
        ctaText: dlCtaTextConstants.SHOW_MORE,
        productCategory,
        subSection,
        cleverTapOverrides: {
          EP_JOURNEY: journeyTag,
          EP_PROPERTY_TYPE: propertyType,
          EP_PRODUCT_NAME: subSection,
          EP_CAMPAIGN: campaignName,
          EP_SPRINT: sprint,
          EP_REFERENCE_ID: referenceId,
          ...(customerId && { EP_CUSTOMER_ID: customerId }),
          EP_SR_NUMBER: componentPosition ?? "3",
          EP_ENTRYPOINT: otherNewsEntryPoint,
        },
      };

      await trackComponent(trackPayload);
    })();

    handleShowMore();
  };

  const renderContent = () => {
    // Loading state for initial load
    if (isLoading && page === 1 && news.length === 0) {
      return (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((num) => (
            <BFLSkeleton
              key={`loading-skeleton-${num}`}
              height={80}
              classes="rounded-lg"
            />
          ))}
        </div>
      );
    }

    // Error state
    if (hasError && news.length === 0) {
      return (
        <div className="flex min-h-[400px] items-center justify-center py-12">
          <BFLApiFailure
            imageSrc={aemContent?.images?.errorImage ?? ""}
            imageAlt={aemContent?.errorImageAlt ?? "Error"}
            error={
              aemContent?.errorTitle ??
              "Unable to fetch the information at this time"
            }
            errorDescription={
              aemContent?.errorDescription ?? "Please try again later"
            }
            retryButtonText={aemContent?.errorRetryButtonText ?? "Retry"}
            onRetry={refetch}
            trackingConfig={{
              sectionTitle: `${aemContent?.componentTitle ?? "Other news"} unable to load`,
              componentPosition: componentPosition ?? "3",
              entryPoint: otherNewsEntryPoint,
              productCategory,
              subSection,
              journeyTag,
              campaignName,
              sprint,
              referenceId,
            }}
            onRetryClick={(params) =>
              trackFailureScenarioRetryClick(params, getCustomerIdForAnalytics)
            }
          />
        </div>
      );
    }

    // Empty state
    if (news.length === 0) {
      return (
        <div className="px-4 py-4">
          <NewsEmptyState
            filters={newsFilters}
            title={aemContent?.emptyStateTitle ?? "No results found"}
            description={
              aemContent?.emptyStateDescription ??
              "Your current filter selection did not match any results."
            }
            emptyDescription={
              aemContent?.emptyStateDescription ?? "No news available"
            }
            aemImages={aemContent?.images}
          />
        </div>
      );
    }

    // Success state - render news list with Show More / Show Less
    return (
      <ListWithShowMore
        showMore={showMore}
        showLess={showLess}
        onShowMore={handleShowMoreWithCt}
        onShowLess={handleShowLess}
      >
        <div ref={newsListRef} className="flex flex-col gap-4">
          {news.map((item) => (
            <CompactNewsCard
              key={item.id}
              newsItem={item}
              showCompanies={true}
              showSource={false}
              className="max-w-full"
              aemImages={aemContent?.images}
              timestampLabels={aemContent?.timestampLabels}
            />
          ))}
          {isLoading &&
            page > 1 &&
            [1, 2, 3].map((num) => (
              <BFLSkeleton
                key={`pagination-skeleton-${num}`}
                height={80}
                width={860}
                classes="rounded-lg"
              />
            ))}
        </div>
      </ListWithShowMore>
    );
  };

  return (
    <div className="flex flex-col gap-3 px-4 py-3 md:gap-5 md:p-[16px]">
      <div>
        {renderHeader()}
        {renderContent()}
      </div>
    </div>
  );
};

export default OtherNews;
